import axios, { AxiosResponse } from 'axios';
import { action, atom, onMount } from 'nanostores';
import {
  ScoreLogic,
  ScoreLogicService,
  CreateScoreLogicRequest,
  EventType,
  EventTypesInfo,
  IndicatorScore,
  IndicatorScoreService,
  CreateIndicatorScoreRequest,
} from '../api';


export const $loadingIndicatorScores = atom<boolean>(false);

export const $indicatorscore = atom<IndicatorScore | undefined>();

export const $indicatorscores = atom<IndicatorScore[]>([]);


/**
 * Main function to load score logics.
 */
export const loadIndicatorScores = async (organizationId: string) => {
  console.log('Retrieving indicator scores...');
  $loadingIndicatorScores.set(true); // Set loading state to true
  await ensureOrganization(organizationId);

  var response = await IndicatorScoreService.getIndicatorScores(organizationId);
  $indicatorscores.set(response ?? []);

  $loadingIndicatorScores.set(false); // Set loading state back to false
};


/**
 * Watch for route changes; if we haven't loaded the score logics, we try to load them.
 */
route.subscribe(async name => {
  // Add a small delay to ensure state is settled
  await Promise.resolve();
  
  const wrongRoute = name !== 'indicatorscores';
  const alreadyLoaded = ($indicatorscores.value?.length ?? 0) > 0;
  
  if (wrongRoute || alreadyLoaded) {
    return;
  }

  const params = $router.get()?.params as Record<'organizationId', string>;
  await loadIndicatorScores(params.organizationId);
});

/**
 * Watch for route changes; load the current selected project result.
 */
// route.subscribe(async name => {
//   if (name !== 'project') {
//     return;
//   }

//   const params = $router.get()?.params as Record<'organizationId', string> &
//     Record<'workspaceId', string> &
//     Record<'projectId', string>;

//   await loadProjectResult(
//     params.organizationId,
//     params.workspaceId,
//     params.projectId,
//   );
// });

/**
 * Main function to load project result.
 */
// const loadProjectResult = async (
//   organizationId: string,
//   workspaceId: string,
//   projectId: string,
// ) => {
//   console.log('Retrieving project result...');

//   await ensureOrganization(organizationId);
//   await ensureWorkspace(workspaceId);
//   await ensureProject(projectId);
//   var projectResult = await ProjectsService.downloadResults(projectId);

//   $projectResult.set(projectResult);
// };

/**
 * Create a new workspace
 * TODO: Include profile selection/invites.
 */
export const createIndicatorScore = action(
  $indicatorscore,
  'createIndicatorScore',
  async (
    store,
    organizationId: string,
    accountReceivableId: string,
    currentScore: number,
  ) => {
    const request: CreateIndicatorScoreRequest = {
      organizationId: organizationId,
      accountReceivableId: accountReceivableId,
      currentScore: currentScore,
    };
    console.log('Creating indicator score...');
    const response = await IndicatorScoreService.createIndicatorScore(request);

    if (!response?.indicatorScore) {
      // error is displayed by caller
      return response;
    }
    
    $indicatorscore.set(response.indicatorScore);

    $indicatorscores.set([response.indicatorScore, ...$indicatorscores.get()]);
    return response;
  },
);

/**
 * Delete's the selected project.
 */
export const deleteIndicatorScore = action(
  $indicatorscore,
  'deleteIndicatorScore',
  async (store, organizationId: string, indicatorscoreId: string) => {
    await IndicatorScoreService.deleteIndicatorScore(organizationId, indicatorscoreId);

    $indicatorscore.set(undefined);

    // Splice out the project
    const indicatorscores = $indicatorscores.get();

    const index = indicatorscores.findIndex(p => p.id === indicatorscoreId);

    if (index > -1) {
      indicatorscores.splice(index, 1);

      $indicatorscores.set(indicatorscores);
    }
  },
);

/**
 * Ensure that the indicator scores is loaded and selected.
 */
export const ensureIndicatorScore = action(
  $indicatorscore,
  'ensureIndicatorScore',
  async (store, indicatorscoreId: string) => {
    let indicatorScore: IndicatorScore | undefined;

    const params = $router.get()?.params as Record<'organizationId', string>;

    if ($indicatorscores.get().length === 0) {
      const indicatorscores = await IndicatorScoreService.getIndicatorScores(
        params.organizationId ?? '',
      );

      $indicatorscores.set(indicatorscores ?? []);
      indicatorScore = indicatorscores?.find(p => p.id === indicatorscoreId);
    } else {
      indicatorScore = $indicatorscores.get().find(p => p.id === indicatorscoreId);
    }

    $indicatorscore.set(indicatorScore);
  },
);  